import * as XLSX from 'xlsx';
import { format } from 'date-fns';

/**
 * Export an array of plain JS objects to an XLSX file.
 *
 * @param rows  An array where each item is a row. Use {} keys you want as column headers.
 * @param sheetName Name of the worksheet (max 31 chars).
 * @param fileName Base filename (without .xlsx). A date stamp is appended automatically.
 * @param columnWidths Optional array of column widths in characters.
 */
export function exportToExcel(
  rows: Record<string, any>[],
  sheetName: string,
  fileName: string,
  columnWidths?: number[],
): void {
  if (!Array.isArray(rows) || rows.length === 0) {
    // eslint-disable-next-line no-alert
    alert('No data to export');
    return;
  }
  const safeSheetName = sheetName.slice(0, 31).replace(/[/\\?*[\]]/g, '_') || 'Sheet1';
  const ws = XLSX.utils.json_to_sheet(rows);
  if (columnWidths) {
    ws['!cols'] = columnWidths.map((wch) => ({ wch }));
  } else {
    // Auto-size based on header length + sample value length
    const sample = rows[0] || {};
    const cols = Object.keys(sample).map((k) => {
      const headerLen = String(k).length;
      const sampleLen = String(sample[k] ?? '').length;
      return { wch: Math.min(60, Math.max(headerLen, sampleLen) + 2) };
    });
    ws['!cols'] = cols;
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
  const stamp = format(new Date(), 'yyyy-MM-dd');
  XLSX.writeFile(wb, `${fileName}_${stamp}.xlsx`);
}

/**
 * Format a Date / ISO string to dd-mmm-yy. Returns '' for missing values.
 */
export function fmtDate(d?: string | Date | null): string {
  if (!d) return '';
  try {
    return format(new Date(d), 'dd-MMM-yy');
  } catch {
    return '';
  }
}

/**
 * Round to nearest rupee (no decimals) for UI display alignment.
 * Excel cells stay numeric so users can still SUM.
 */
export function exportAmount(n: number | string | null | undefined): number {
  const v = Number(n ?? 0);
  return Math.round(isFinite(v) ? v : 0);
}
